import type { JSX } from 'react';

import {
  Permission,
  planLinkReasonFor,
  subscriptionPlanPath,
  type EnforcedPlanFeature,
} from '@bookora/shared';
import { Icon, PageHeader, Typography } from '@bookora/ui';
import { useQueryClient } from '@tanstack/react-query';

import type { PlanResponse } from '@/api/generated/model';
import { useAuth } from '@/auth/token-store';
import { LinkButton } from '@/components/link-button';
import { settingsKeys } from '@/features/settings/hooks';
import { cheapestFlatPlan } from '@/features/settings/subscription/catalogue-model';
import { useCan } from '@/permissions/use-can';

import { usePlanLock, type PlanLock } from './plan-access';
import {
  COMPARE_PLANS_LABEL,
  FEATURE_PITCH_FA,
  HOLDER_NOTE,
  NON_HOLDER_LINE,
  UNKNOWN_PLAN_NAME,
  currentPlanLine,
  featureLockTitle,
  lockTitle,
} from './plan-lock-copy';

import './plan-lock.css';

export type PlanLockedVariant = 'section' | 'tab' | 'card';

export interface PlanLockedStateProps {
  feature: EnforcedPlanFeature;
  variant: PlanLockedVariant;
  /** The section's name, for the `section` variant's page header. */
  sectionLabel?: string;
  /**
   * The SERVER already said this is locked (a report's `locked`, a 402) —
   * draw the lock even while plan access is loading or says open, with
   * `upgradePlanKey` as the plan to compare when plan access has none.
   */
  force?: boolean;
  upgradePlanKey?: string | null;
}

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  «این بخش در پلن {plan} است» — the locked screen (owner D6, critic G5)
 * ════════════════════════════════════════════════════════════════════════════
 *
 * What a clinic sees in place of a screen its plan does not include, and has
 * never used (a feature it has records of is read-only instead — see
 * `plan-lock-banner.tsx`). It is an explanation, never a dead end:
 *
 *  · the pitch says what the feature DOES, in the clinic's words;
 *  · the current-plan line says where the clinic stands (trial, RaaS, none);
 *  · a `settings.billing` holder gets ONE ink primary, «مقایسهٔ پلن‌ها», to the
 *    plan card that has it (`subscriptionPlanPath(requiredPlanKey, reason)` —
 *    the subscription tab highlights that card «پیشنهاد برای …»), plus the
 *    note that nothing is charged or changed by looking;
 *  · everyone else is told plainly that the upgrade is «مدیر کلینیک»'s, with
 *    no button at all — a button they cannot use would be a lie.
 *
 * Variants: `section` (a whole route; a page header, then the card), `tab`
 * (the same body, compact, inside a tab strip), `card` (one line, for a
 * dashboard card or an «add» tile). Renders nothing while the feature is
 * open — the lock UI fails open (`plan-access.ts`).
 */
export function PlanLockedState({
  feature,
  variant,
  sectionLabel,
  force = false,
  upgradePlanKey = null,
}: PlanLockedStateProps): JSX.Element | null {
  const lock = usePlanLock(feature);
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  if (lock.state === 'open' && !force) return null;

  const planName = lock.requiredPlanName ?? UNKNOWN_PLAN_NAME;
  const upgradeTo = subscriptionPlanPath(
    lock.requiredPlanKey ?? upgradePlanKey,
    planLinkReasonFor(feature),
  );

  if (variant === 'card') {
    return (
      <div className="bk-plan-lock-card" role="note">
        <span className="bk-plan-lock-card__icon" aria-hidden>
          <Icon name="lock" size={16} />
        </span>
        <Typography.Text strong>{featureLockTitle(feature, planName)}</Typography.Text>
        {canBilling ? (
          <LinkButton size="small" to={upgradeTo}>
            {COMPARE_PLANS_LABEL}
          </LinkButton>
        ) : (
          <Typography.Text type="secondary">{NON_HOLDER_LINE}</Typography.Text>
        )}
      </div>
    );
  }

  const body = (
    <LockBody
      feature={feature}
      lock={lock}
      planName={planName}
      canBilling={canBilling}
      upgradeTo={upgradeTo}
      compact={variant === 'tab'}
    />
  );

  if (variant === 'section') {
    return (
      <>
        {sectionLabel ? <PageHeader title={sectionLabel} /> : null}
        {body}
      </>
    );
  }
  return body;
}

/**
 * The entry plan's name from the plan list if this session already loaded it
 * (the subscription tab's `usePlans`) — read from the cache, never fetched:
 * the lock must not wait on, or fail with, a second request.
 */
function useCachedEntryPlanName(): string | null {
  const { activeClinicId } = useAuth();
  const client = useQueryClient();
  const plans = client.getQueryData<PlanResponse[]>(settingsKeys.plans(activeClinicId));
  return plans ? (cheapestFlatPlan(plans)?.name ?? null) : null;
}

function LockBody({
  feature,
  lock,
  planName,
  canBilling,
  upgradeTo,
  compact,
}: {
  feature: EnforcedPlanFeature;
  lock: PlanLock;
  planName: string;
  canBilling: boolean;
  upgradeTo: string;
  compact: boolean;
}): JSX.Element {
  const title = lockTitle(planName);
  const entryPlanName = useCachedEntryPlanName();
  return (
    <section
      className={compact ? 'bk-plan-lock bk-plan-lock--tab' : 'bk-plan-lock'}
      aria-label={title}
    >
      <div className="bk-plan-lock__body">
        <span className="bk-plan-lock__icon" aria-hidden>
          <Icon name="lock" size={compact ? 20 : 24} />
        </span>
        {/* A heading below the page's own h1 (and the tab strip's). */}
        <h2 className="bk-plan-lock__title">{title}</h2>
        <Typography.Paragraph style={{ margin: 0 }}>
          {FEATURE_PITCH_FA[feature]}
        </Typography.Paragraph>
        {/* Plan access knows nothing yet (a forced lock): no guess at the plan. */}
        {lock.state !== 'open' ? (
          <span className="bk-plan-lock__muted">{currentPlanLine(lock, entryPlanName)}</span>
        ) : null}
        {canBilling ? (
          <>
            <LinkButton
              type="primary"
              icon={<Icon name="arrow-forward" size={16} />}
              to={upgradeTo}
            >
              {COMPARE_PLANS_LABEL}
            </LinkButton>
            <span className="bk-plan-lock__muted">{HOLDER_NOTE}</span>
          </>
        ) : (
          <Typography.Text strong>{NON_HOLDER_LINE}</Typography.Text>
        )}
      </div>
    </section>
  );
}
